import { LogManager } from '@src/utility/Logger';

import { err, ok } from './common/Result';

import type { Result } from './common/Result';
import type {
  TranslationMemoryCollection,
  TranslationMemoryCollectionInput,
  TranslationMemoryCollectionRepository,
} from '@src/repositories/TranslationMemoryCollectionRepository';

const logger = LogManager.getLogger('service:TranslationMemoryCollection');

type RepositoryDeps = Pick<
  TranslationMemoryCollectionRepository,
  'fetchForShowcase' | 'create' | 'update' | 'delete'
>;

/**
 * Orchestrates the flat translation-memory collection CRUD over
 * `TranslationMemoryCollectionRepository`, returning `Result`s. Holds a lazy
 * repository getter because repositories are constructed by `DatoClientStore`
 * only after auth is wired (same indirection as `DistributionChannelService`).
 */
export class TranslationMemoryCollectionService {
  constructor(private readonly getRepository: () => RepositoryDeps) {}

  fetchForShowcase(
    showcaseId: string,
  ): Promise<Result<TranslationMemoryCollection[]>> {
    if (!showcaseId)
      return Promise.resolve(err('VALIDATION', 'showcaseId is required'));
    return this.run('load translation memory collections', repo =>
      repo.fetchForShowcase(showcaseId),
    );
  }

  create(
    showcaseId: string,
    input: TranslationMemoryCollectionInput,
    personId: string | null,
  ): Promise<Result<TranslationMemoryCollection>> {
    if (!showcaseId)
      return Promise.resolve(err('VALIDATION', 'showcaseId is required'));
    if (!input.name.trim()) {
      return Promise.resolve(err('VALIDATION', 'name is required'));
    }
    return this.run('create translation memory collection', async repo => {
      const created = await repo.create(showcaseId, input, personId);
      if (!created) throw new Error('Collection create returned null');
      return created;
    });
  }

  update(
    id: string,
    input: TranslationMemoryCollectionInput,
    personId: string | null,
  ): Promise<Result<TranslationMemoryCollection>> {
    if (!id) return Promise.resolve(err('VALIDATION', 'id is required'));
    if (!input.name.trim()) {
      return Promise.resolve(err('VALIDATION', 'name is required'));
    }
    return this.run('update translation memory collection', async repo => {
      const updated = await repo.update(id, input, personId);
      if (!updated) throw new Error('Collection update returned null');
      return updated;
    });
  }

  delete(id: string): Promise<Result<void>> {
    if (!id) return Promise.resolve(err('VALIDATION', 'id is required'));
    return this.run('delete translation memory collection', async repo => {
      const ok = await repo.delete(id);
      if (!ok) throw new Error('Collection delete failed');
    });
  }

  private async run<T>(
    description: string,
    fn: (repo: RepositoryDeps) => Promise<T>,
  ): Promise<Result<T>> {
    let repository: RepositoryDeps;
    try {
      repository = this.getRepository();
    } catch (e) {
      logger.error(
        'Translation memory collection repository not initialized',
        e,
      );
      return err(
        'PRECONDITION',
        e instanceof Error ? e.message : 'Repository not initialized',
        e,
      );
    }
    try {
      return ok(await fn(repository));
    } catch (e) {
      logger.error(`Failed to ${description}`, e);
      return err(
        'EXTERNAL',
        e instanceof Error ? e.message : 'Unknown error',
        e,
      );
    }
  }
}
